import useEnforceLightMode from "../hooks/useEnforceLightMode";

export default function TermsOfServicePage() {
  useEnforceLightMode();
  return (
    <div className="min-h-screen bg-[#f4f4f2] dark:bg-dark-950">
      <div className="flex-1 flex flex-col relative overflow-hidden py-8 sm:py-12 lg:py-16">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.04)_1px,transparent_1px)] bg-[size:44px_44px]" />
        <div className="flex-1 flex justify-center p-4 sm:p-6 lg:p-8">
          <div className="relative z-10 w-full max-w-4xl">
            <div className="bg-white dark:bg-dark-900 border-2 border-zinc-900 dark:border-dark-700 p-6 sm:p-8 lg:p-10 space-y-6 sm:space-y-8">
              {/* Header */}
              <div className="text-center space-y-4 sm:space-y-6">
                <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 bg-zinc-900 flex items-center justify-center border-2 border-zinc-900">
                  <svg
                    className="w-7 h-7 sm:w-8 sm:h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white">
                  Terms of Service
                </h1>
                <p className="text-base sm:text-lg text-gray-600 dark:text-dark-400">
                  Last updated: August 14, 2026
                </p>
              </div>

              {/* Content */}
              <div className="space-y-6 sm:space-y-8 prose prose-sm sm:prose-base max-w-none">
                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Agreement to Terms
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    These Terms govern your access to and use of services provided by DealHawk, LLC, including DealHawk websites and mobile apps and the Penny Meals mobile app. By using a service, you agree to these Terms. If you do not agree, do not use the service.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Use License
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    Permission is granted to temporarily access and use our service for personal, non-commercial use. This is the grant of a license, not a transfer of title, and under this license you may not:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base text-gray-700 dark:text-dark-300">
                    <li>Modify or copy the materials</li>
                    <li>Use the materials for any commercial purpose or for any public display</li>
                    <li>Attempt to reverse engineer any software contained in the service</li>
                    <li>Remove any copyright or other proprietary notations from the materials</li>
                    <li>Transfer the materials to another person or "mirror" the materials on any other server</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Third-Party Retailers
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    <strong>Important:</strong> We are an independent service and are not affiliated with, endorsed by, or officially connected to any retailer whose products or information may appear on our service. All product information, pricing, and availability are provided for informational purposes only and are subject to change without notice.
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    Any trademarks, service marks, trade names, product names, and logos displayed on our service are the property of their respective owners. The use of any such marks does not imply any affiliation with or endorsement by those retailers.
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    Retailer logos displayed throughout the service are provided by{" "}
                    <a
                      href="https://brandfetch.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zinc-900 dark:text-white hover:text-zinc-700 dark:hover:text-zinc-300 font-semibold"
                    >
                      Brandfetch
                    </a>.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Payment and Refund Policy
                  </h2>
                  <div className="bg-zinc-50 dark:bg-dark-800 p-4 sm:p-6 border-2 border-zinc-900 dark:border-dark-600">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Refund Requests
                    </h3>
                    <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                      For eligible purchases made directly through a DealHawk website, you may request a refund within 7 days of the initial purchase by contacting{" "}
                      <a
                        href="mailto:admin@dealhawkpro.io"
                        className="text-zinc-900 dark:text-white hover:text-zinc-700 dark:hover:text-zinc-300 font-semibold"
                      >
                        admin@dealhawkpro.io
                      </a>
                      {" "}with your account information and reason for the request. Purchases made through Apple are subject to Apple&apos;s payment and refund policies and must be requested through Apple.
                    </p>
                    <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed">
                      Approved direct-purchase refunds are returned to the original payment method. Statutory rights are not affected.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Payment Processing
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    Eligible website payments are processed by Stripe. Eligible mobile-app subscriptions are processed by Apple through the App Store or Google through Google Play, with RevenueCat used to validate subscription status and entitlements. We do not collect or store your complete payment-card information.
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    Your purchase is also governed by the terms and policies of the payment platform you use. Billing issues for App Store or Google Play purchases should be handled through the applicable store account or support service; our support team can assist with access and entitlement questions.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Subscription Terms
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    If you purchase a subscription, your payment account will be charged at confirmation of purchase and on a recurring monthly or annual basis, depending on the plan you select. The price and billing period are shown before purchase. An introductory trial, when offered and when you are eligible, converts to the displayed paid subscription unless you cancel before the trial ends. Subscriptions renew automatically unless canceled at least 24 hours before the end of the current period. App Store subscriptions can be managed or canceled in your Apple account settings. Cancellation takes effect at the end of the paid period, and deleting the app does not cancel a subscription.
                  </p>
                </section>

                <section id="referral-program">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Referral Program
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    Eligible DealHawk members may share a personal referral link with a new member. When the new member joins through that link and completes their first successful paid monthly subscription, the new member receives 50% off their first paid month when eligible and the inviter earns one credit worth 50% off one monthly renewal. A referral reward is issued only after the payment provider confirms a positive, production payment.
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base text-gray-700 dark:text-dark-300 mb-4">
                    <li>An inviter may earn no more than 12 referral credits per UTC calendar year.</li>
                    <li>Only one credit may be applied to a monthly renewal. Credits have no cash value, are non-transferable, and cannot be combined with another discount when the billing provider does not permit it.</li>
                    <li>Stripe credits are scheduled automatically. App Store and Google Play credits remain banked until the member confirms the promotional subscription change in the applicable store purchase sheet. Store eligibility and billing rules also apply.</li>
                    <li>Self-referrals, duplicate or misleading accounts, fraudulent or manipulated payments, spam, and other attempts to abuse the program do not qualify.</li>
                    <li>If the qualifying payment is refunded, reversed, disputed, or charged back, the associated referral credit will be removed. If it was already used, an equivalent future referral credit may be withheld.</li>
                  </ul>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    DealHawk may investigate referral activity, deny or reverse rewards that do not meet these rules, and suspend or end the program or an account&apos;s participation when reasonably necessary to prevent abuse, comply with provider rules, or protect the service. Changes apply prospectively unless a reward was obtained through abuse, error, refund, reversal, dispute, or chargeback.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    User Accounts
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    When you create an account, you are responsible for maintaining the security of your account and password. You agree to accept responsibility for all activities that occur under your account. You must notify us immediately of any unauthorized use of your account.
                  </p>
                </section>

                <section id="community-success">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Community Success Guidelines
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    Community Success lets members share real purchases they made with DealHawk. It is for members 18 and older and is not a place for adult or sexual content. The first time you open it, you confirm that you are at least 18, and we save that confirmation on your account. Free members can view the feed. Posting photos and comments requires a paid plan. By posting a photo, message, or comment, you agree to these guidelines and understand that DealHawk may screen, review, refuse, remove, or restrict content or accounts that violate them.
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base text-gray-700 dark:text-dark-300">
                    <li>Share only your own, genuine purchase success and a photo you have permission to use.</li>
                    <li>Keep it helpful and respectful. Do not post harassment, hate, threats, bullying, sexual content, graphic material, or anything illegal or unsafe.</li>
                    <li>Do not post photos of minors or content that exploits, endangers, or reveals personal information about a minor.</li>
                    <li>Do not post spam, scams, misleading promotions, resale offers, affiliate links, or requests to buy or sell goods.</li>
                    <li>Do not include personal, financial, account, location, or other sensitive information—yours or anyone else&apos;s. Cover receipts, labels, and screens that reveal it.</li>
                    <li>Do not post content that infringes another person&apos;s copyright, trademark, privacy, publicity, or other rights.</li>
                    <li>Use in-app report and block controls when content or a member concerns you. We review reports and may remove content or take account action.</li>
                  </ul>
                </section>

                <section id="community-content-license">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Your Community Content and License
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    You keep ownership of photos, messages, comments, and other content you submit to Community Success ("Community Content"). You represent that you own your Community Content or have all permissions needed to submit it, that any identifiable person shown has consented where consent is required, and that your Community Content does not violate any law or another person&apos;s intellectual-property, privacy, publicity, or other rights.
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    When you submit Community Content, you grant DealHawk a worldwide, non-exclusive, royalty-free, sublicensable license to host, store, reproduce, format, resize, compress, display, distribute, and otherwise process that content as needed to operate, secure, moderate, and provide DealHawk services. This license allows our service providers to process the content for those purposes. It continues while the content is hosted and for a reasonable period in backups, logs, moderation records, or where retention is required for safety, dispute resolution, or legal compliance.
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    Community Content is shared content, not private communication. Native DealHawk posts and comments can be removed using the available in-app controls. Deleting your account does not automatically remove Community Content you previously shared, so delete it first or contact us to request removal. Content submitted through a DealHawk community channel that clearly identifies itself as a source for Community Success may also be copied into the feed; contact us if you need an imported post removed.
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    We do not endorse member content and do not guarantee that every item will be reviewed. We may use automated tools and human review, decline to publish content, remove content, preserve evidence of abuse, or suspend or terminate access when reasonably necessary to enforce these Terms or protect members and the service. To report alleged copyright or other rights infringement, email{" "}
                    <a
                      href="mailto:admin@dealhawkpro.io"
                      className="text-zinc-900 dark:text-white hover:text-zinc-700 dark:hover:text-zinc-300 font-semibold"
                    >
                      admin@dealhawkpro.io
                    </a>
                    {" "}with enough information for us to identify the content and your rights.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Prohibited Uses
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    You agree not to use the service:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base text-gray-700 dark:text-dark-300">
                    <li>In any way that violates any applicable law or regulation</li>
                    <li>To transmit any malicious code or viruses</li>
                    <li>To attempt to gain unauthorized access to any part of the service</li>
                    <li>To interfere with or disrupt the service or servers connected to the service</li>
                    <li>To use any automated system to access the service without permission</li>
                    <li>To impersonate any person or entity</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Disclaimer
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    The information on this service is provided on an "as is" basis. To the fullest extent permitted by law, we exclude all representations, warranties, and conditions relating to our service and the use of this service.
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    We do not guarantee the accuracy, completeness, or usefulness of any information on the service. Product prices, availability, and specifications are subject to change without notice and may differ from what is displayed on our service.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Limitation of Liability
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    In no event shall we be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the service.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Changes to Terms
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Contact Information
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-dark-300 leading-relaxed mb-4">
                    If you have any questions about these Terms of Service, please contact us at{" "}
                    <a
                      href="mailto:admin@dealhawkpro.io"
                      className="text-zinc-900 dark:text-white hover:text-zinc-700 dark:hover:text-zinc-300 font-semibold"
                    >
                      admin@dealhawkpro.io
                    </a>
                  </p>
                </section>
              </div>

              {/* Back to App */}
              <div className="text-center pt-4 sm:pt-6">
                <a
                  href="/"
                  className="inline-flex items-center justify-center space-x-2 bg-zinc-900 hover:bg-black text-white font-semibold py-3 px-6 sm:py-4 sm:px-8 rounded-none transition-colors text-sm sm:text-base w-full sm:w-auto border-2 border-zinc-900"
                >
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                  <span>Back to App</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
